// Approximate matches are suggestions only. Never use this search to resolve/save identity.
export const normalizarBusqueda = value => String(value ?? '').normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '').toUpperCase().replace(/[^A-Z0-9]+/g, ' ').trim();
const numero = value => String(value ?? '').trim().replace(/^0+(?=\d)/, '');
const colegio = value => {
  const text=normalizarBusqueda(value);
  return text.includes('OBST') || text==='COP' ? 'COP' : text.includes('MEDIC') || text==='CMP' ? 'CMP' : text;
};
const indexes=new WeakMap();
export function indicePrescriptores(personas) {
  if(indexes.has(personas)) return indexes.get(personas);
  const unique=new Map();
  for(const persona of personas) {
    const nombre=normalizarBusqueda(persona.prescriptor), tipo=colegio(persona.tipoColegiatura);
    const cmp=numero(persona.colegiatura);
    const key=JSON.stringify([nombre,cmp,tipo]);
    if(nombre && !unique.has(key)) unique.set(key,{persona,nombre,tokens:nombre.split(' ').slice(0,16),cmp,tipo});
  }
  const index=[...unique.values()]; indexes.set(personas,index); return index;
}

// Bounded optimal-string-alignment distance also tolerates swapped adjacent letters.
export function distanciaCorta(a,b,maximo=2) {
  if(a===b) return 0;
  if(Math.abs(a.length-b.length)>maximo) return maximo+1;
  let anterior=Array.from({length:b.length+1},(_,i)=>i), dosAntes=null;
  for(let i=1;i<=a.length;i++) {
    const actual=[i]; let minimo=i;
    for(let j=1;j<=b.length;j++) {
      actual[j]=Math.min(anterior[j]+1,actual[j-1]+1,anterior[j-1]+(a[i-1]===b[j-1]?0:1));
      if(dosAntes && i>1 && j>1 && a[i-1]===b[j-2] && a[i-2]===b[j-1]) actual[j]=Math.min(actual[j],dosAntes[j-2]+1);
      minimo=Math.min(minimo,actual[j]);
    }
    if(minimo>maximo) return maximo+1;
    dosAntes=anterior; anterior=actual;
  }
  return anterior[b.length];
}
function matchWord(query,word) {
  if(word===query) return {errores:0,orden:0};
  if(word.startsWith(query)) return {errores:0,orden:5};
  if(word.includes(query)) return {errores:0,orden:10};
  if(query.length<4 || word.length>48 || query.length>48) return null;
  const limit=query.length>=8 ? 2 : 1;
  const errores=distanciaCorta(query,word,limit);
  return errores<=limit ? {errores,orden:20+errores*20} : null;
}
export function matchNames(queries,words) {
  if(!queries.length) return {errores:0,orden:0};
  if(queries.length>words.length || queries.length>8) return null;
  const candidates=queries.map(q=>words.map((word,index)=>({index,match:matchWord(q,word)}))
    .filter(x=>x.match).sort((a,b)=>a.match.orden-b.match.orden));
  if(candidates.some(x=>!x.length)) return null;
  // Each query word must match a different name word (no repeated-word false positives).
  const owners=new Map();
  const assign=(qi,seen)=>{
    for(const candidate of candidates[qi]) {
      if(seen.has(candidate.index)) continue;
      seen.add(candidate.index);
      if(!owners.has(candidate.index) || assign(owners.get(candidate.index),seen)) {owners.set(candidate.index,qi);return true;}
    }
    return false;
  };
  const order=queries.map((_,i)=>i).sort((a,b)=>candidates[a].length-candidates[b].length);
  for(const qi of order) if(!assign(qi,new Set())) return null;
  let errores=0,orden=0;
  for(const [wi,qi] of owners) {const result=matchWord(queries[qi],words[wi]);errores+=result.errores;orden+=result.orden;}
  return errores<=2 ? {errores,orden} : null;
}

export function buscarPrescriptores(index,input,limit=30) {
  const query=normalizarBusqueda(String(input ?? '').slice(0,120));
  if(!query) return [];
  const parts=query.match(/[A-Z]+|\d+/g) || [];
  const requestedTypes=parts.filter(x=>x==='CMP'||x==='COP');
  if(requestedTypes.length>1) return [];
  const digits=parts.filter(x=>/^\d+$/.test(x));
  if(digits.length>1) return [];
  const numberQuery=digits.length ? numero(digits[0]) : '';
  const words=parts.filter(x=>!/^[0-9]+$/.test(x) && x!=='CMP' && x!=='COP');
  if(!words.length && !numberQuery) return [];
  const results=[];
  for(const entry of index) {
    if(requestedTypes.length && entry.tipo!==requestedTypes[0]) continue;
    let numberErrors=0,numberOrder=0;
    if(numberQuery) {
      if(entry.cmp===numberQuery) numberOrder=0;
      else if(entry.cmp.startsWith(numberQuery)) numberOrder=5;
      else if(entry.cmp.includes(numberQuery)) numberOrder=10;
      else if(numberQuery.length>=5 && /^\d+$/.test(entry.cmp) && distanciaCorta(numberQuery,entry.cmp,1)<=1) {numberErrors=1;numberOrder=40;}
      else continue;
    }
    const nameMatch=matchNames(words,entry.tokens);
    if(!nameMatch || nameMatch.errores+numberErrors>2) continue;
    const aproximada=Boolean(nameMatch.errores || numberErrors);
    const score=(aproximada?1000:0)+nameMatch.orden+numberOrder+(entry.nombre===query?-20:0);
    results.push({...entry.persona,_coincidencia:{aproximada,score,
      motivo:numberErrors ? (nameMatch.errores?'Nombre y colegiatura similares':'Colegiatura similar') : 'Nombre similar',
      grupo:aproximada?'Posibles coincidencias · confirma los datos':'Coincidencias'}});
  }
  return results.sort((a,b)=>a._coincidencia.score-b._coincidencia.score ||
    normalizarBusqueda(a.prescriptor).localeCompare(normalizarBusqueda(b.prescriptor)) || String(a.colegiatura).localeCompare(String(b.colegiatura))).slice(0,limit);
}
